import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { repository } from '@/api/repository'
import type { Incident } from '@/api/types'
import { messageFor } from '@/api/errors'
import { groupIncidents } from '@/domain/grouping'

interface State {
  /** Las más recientes primero, tal como llegan; el orden de pantalla es `groups`. */
  items: Incident[]
  /** Puede haber incidencias más antiguas que no entraron al tope. */
  truncated: boolean
  loading: boolean
  /** Solo la primera carga. */
  initial: boolean
  /** Relectura sobre datos que ya están en pantalla: no vacía la lista. */
  refreshing: boolean
  error: string | null
}

const INITIAL: State = {
  items: [],
  truncated: false,
  loading: false,
  initial: true,
  refreshing: false,
  error: null,
}

/**
 * El listado se carga una sola vez, hasta `LIST_LIMIT` incidencias, sin
 * páginas siguientes. Después solo se relee: entero con `refresh`, o una
 * incidencia con `refreshOne`.
 */
export function useIncidents() {
  const [state, setState] = useState<State>(INITIAL)
  // Que el botón de actualizar y la carga inicial no se pisen.
  const inFlight = useRef(false)

  const load = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setState({ ...INITIAL, loading: true })

    try {
      const listing = await repository.loadIncidents()
      setState((s) => ({
        ...s,
        items: listing.incidents,
        truncated: listing.truncated,
        loading: false,
        initial: false,
        error: null,
      }))
    } catch (err) {
      setState((s) => ({ ...s, loading: false, initial: false, error: messageFor(err) }))
    } finally {
      inFlight.current = false
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const retry = useCallback(() => {
    void load()
  }, [load])

  /**
   * Vuelve a pedir el listado.
   *
   * Se distingue de `load` en que **no vacía la lista mientras espera**: el
   * usuario dijo "actualizá", no "borrá y volvé a empezar", y dejar la
   * pantalla en blanco durante la ida y vuelta se lee como si algo hubiera
   * fallado. La lista vieja se reemplaza recién cuando llega la nueva.
   */
  const refresh = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    setState((s) => ({ ...s, refreshing: true, error: null }))

    try {
      const listing = await repository.loadIncidents()
      setState((s) => ({
        ...s,
        items: listing.incidents,
        truncated: listing.truncated,
        initial: false,
        refreshing: false,
        error: null,
      }))
    } catch (err) {
      setState((s) => ({ ...s, refreshing: false, error: messageFor(err) }))
    } finally {
      inFlight.current = false
    }
  }, [])

  /**
   * Relee una sola incidencia y la reemplaza en su lugar.
   *
   * Es lo que corre al elegir una tarjeta: la tarjeta del listado y la sección
   * de detalle salen del mismo elemento, así que actualizar acá las pone al día
   * a las dos. Si cambió de prioridad o de área, `groups` la reubica solo. Si
   * la incidencia dejó de pertenecer al listado —se cerró o se rechazó
   * mientras el portal estaba abierto— se saca en vez de mostrarla con un
   * estado que el portal no lista.
   *
   * Un fallo no rompe nada: se conserva lo que ya estaba en pantalla, que sigue
   * siendo cierto aunque esté viejo.
   */
  const refreshOne = useCallback(async (incidentId: string) => {
    try {
      const fresh = await repository.reloadIncident(incidentId)
      setState((s) => {
        if (!s.items.some((i) => i._id === incidentId)) return s
        return {
          ...s,
          items: fresh
            ? s.items.map((i) => (i._id === incidentId ? fresh : i))
            : s.items.filter((i) => i._id !== incidentId),
        }
      })
    } catch {
      /* se deja lo que hay: viejo es mejor que vacío */
    }
  }, [])

  const groups = useMemo(() => groupIncidents(state.items), [state.items])

  return { ...state, groups, retry, refresh, refreshOne }
}
